import { watch } from 'vue'
import i18n from './i18n'
import { useContentLang } from '@/core/helpers/useContentLang'

/**
 * Koppelt die vue-i18n-Locale an die Inhaltssprache.
 *
 * Vorher gab es ZWEI Quellen für „die Sprache": die Oberfläche stand auf dem
 * Build-Wert aus VITE_DEFAULT_LOCALE, die Inhalte (Rechtstexte, Vorlagen) auf
 * `user.settings.language`. Solange niemand $t() benutzt, fällt das nicht auf —
 * sobald die Oberfläche übersetzt wird, bliebe sie deutsch, während die Inhalte
 * umschalten. Ab hier gilt die Nutzereinstellung für beides.
 *
 * Die Richtung ist bewusst einseitig: Inhaltssprache → vue-i18n, nie umgekehrt.
 * Welche Sprachen es gibt, entscheidet mgmt_languages im Management; die
 * Locale-JSONs sind nur die Teilmenge, für die es auch Oberflächentexte gibt.
 *
 * Zwei Sonderfälle, die genau deshalb auftreten können:
 *   1. Der Nutzer wählt eine Sprache, für die es KEINE Locale-Datei gibt
 *      (freie Sprachliste, z.B. fr). Dann bleibt die Oberfläche auf der
 *      Basissprache statt auf einer fremden dritten Sprache zu landen — die
 *      Inhalte selbst kommen trotzdem übersetzt, denn die kommen aus der DB.
 *   2. Vor dem ersten Sync ist die Basissprache noch der Fallback-Wert aus
 *      contentLang.ts. Der Watcher läuft immediate und danach bei jeder
 *      Änderung erneut, holt sich das also selbst wieder ein.
 *
 * Wird EINMAL beim App-Start aufgerufen (main.ts, nach app.use(pinia)) — der
 * Watcher lebt bewusst so lange wie die App und wird nie gestoppt.
 */
/**
 * Reine Entscheidungsfunktion — welche Locale und welcher Fallback ergeben sich
 * aus Wunschsprache, Basissprache und den mitgelieferten Locale-Dateien.
 *
 * Bewusst frei von i18n- und Store-Zugriffen, damit sie ohne laufende App
 * testbar ist. `null` heißt jeweils „nicht anfassen, aktuellen Wert behalten".
 */
export function resolveLocale(
  wanted: string,
  base: string,
  available: string[],
): { locale: string | null; fallback: string | null } {
  const has = (code: string) => !!code && available.includes(code)
  return {
    locale:   has(wanted) ? wanted : (has(base) ? base : null),
    fallback: has(base) ? base : null,
  }
}

export function initLocaleBridge() {
  const { lang, base } = useContentLang()

  watch(
    [lang, base],
    () => {
      /** Sprachcodes, für die überhaupt Oberflächentexte mitgeliefert werden. */
      const available = Object.keys(i18n.global.messages.value || {})
      const { locale, fallback } = resolveLocale(lang.value, base.value, available)

      // Fallback zuerst: UI und Inhalte sollen auf dieselbe Sprache
      // zurückfallen. Kennt vue-i18n die Basissprache nicht, bleibt der
      // konfigurierte Wert stehen — irgendein Fallback ist besser als keiner.
      if (fallback) (i18n.global.fallbackLocale as any).value = fallback
      if (locale && i18n.global.locale.value !== locale) i18n.global.locale.value = locale
    },
    { immediate: true },
  )
}
